import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ExpenseService } from '@/services/expense.service';
import { VendorService } from '@/services/vendor.service';
import {
  Expense,
  ExpenseCategory,
  ExpenseCategoryLabels,
  ExpenseCategoryGroups,
  ExpenseStatus,
  PaymentMethod,
  PaymentMethodLabels,
  MileageDetails,
} from '@/models/expense.model';
import { Vendor } from '@/models/vendor.model';
import { format } from 'date-fns';

type FormState = Partial<Expense>;

export const ExpenseForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  const [formData, setFormData] = useState<FormState>({
    category: ExpenseCategory.other,
    paymentMethod: PaymentMethod.personalCard,
    currency: 'USD',
    expenseDate: new Date(),
    billable: false,
    taxDeductible: true,
    receiptRequired: true,
    isRecurring: false,
    tags: [],
    attachments: [],
    policyViolations: [],
    approvalWorkflow: [],
  });
  const [tagInput, setTagInput] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showMileageFields, setShowMileageFields] = useState(false);
  const [mileageData, setMileageData] = useState<Partial<MileageDetails>>({
    ratePerMile: 0.67, // Current IRS rate
    roundTrip: false,
  });
  const [vendorSearch, setVendorSearch] = useState('');
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);

  const { data: expense, isLoading } = useQuery({
    queryKey: ['expense', id],
    queryFn: () => (id ? ExpenseService.getExpenseById(id) : null),
    enabled: isEditing && Boolean(id),
  });

  const { data: vendors = [], isLoading: vendorsLoading } = useQuery({
    queryKey: ['vendors'],
    queryFn: () => VendorService.getAllVendors(),
    enabled: !!profile,
  });

  useEffect(() => {
    if (expense) {
      setFormData(expense);
      if (expense.mileageDetails) {
        setShowMileageFields(true);
        setMileageData(expense.mileageDetails);
      }
      if (expense.vendorName) {
        setVendorSearch(expense.vendorName);
      }
    }
  }, [expense]);

  useEffect(() => {
    // Show mileage fields for mileage category
    if (formData.category === ExpenseCategory.mileage) {
      setShowMileageFields(true);
    }
  }, [formData.category]);

  const mutation = useMutation({
    mutationFn: async (data: FormState) => {
      console.log('[ExpenseForm] Starting expense save/update', { isEditing, data: { ...data, vendorId: data.vendorId, vendorName: data.vendorName } });
      console.log('[ExpenseForm] User profile:', { 
        id: profile?.id, 
        email: profile?.email, 
        role: profile?.role, 
        status: profile?.status,
        isActive: profile?.isActive 
      });
      
      // Check if user has valid operations profile
      if (!profile) {
        throw new Error('User profile not found. Please log out and log back in.');
      }
      
      if (profile.status !== 'active') {
        throw new Error(`Your account status is "${profile.status}". Only active users can create expenses. Please contact an administrator.`);
      }
      
      if (!data.description?.trim()) throw new Error('Description is required');
      if (!data.amount || data.amount <= 0) throw new Error('Valid amount is required');

      // Add mileage details if applicable
      if (showMileageFields && mileageData.miles) {
        data.mileageDetails = {
          startLocation: mileageData.startLocation || '',
          endLocation: mileageData.endLocation || '',
          miles: mileageData.miles,
          ratePerMile: mileageData.ratePerMile || 0.67,
          roundTrip: mileageData.roundTrip || false,
          purpose: mileageData.purpose || '',
        };
        // Calculate amount from miles
        data.amount = mileageData.miles * (mileageData.ratePerMile || 0.67) * (mileageData.roundTrip ? 2 : 1);
      }

      // Add employee info from profile
      data.employeeId = profile?.id || '';
      data.employeeName = profile?.fullName || profile?.email || '';
      data.employeeEmail = profile?.email || '';
      
      // Ensure status is set (default to draft if not provided)
      if (!data.status) {
        data.status = ExpenseStatus.draft;
      }
      
      // Ensure createdBy is set
      if (!data.createdBy && profile?.id) {
        data.createdBy = profile.id;
      }

      console.log('[ExpenseForm] Prepared expense data:', { 
        vendorId: data.vendorId, 
        vendorName: data.vendorName,
        status: data.status,
        employeeId: data.employeeId,
        createdBy: data.createdBy
      });

      try {
        if (isEditing && id) {
          console.log('[ExpenseForm] Updating expense:', id);
          await ExpenseService.updateExpense(id, data);
          console.log('[ExpenseForm] Expense updated successfully');
          return id;
        }
        console.log('[ExpenseForm] Creating new expense');
        const expenseId = await ExpenseService.createExpense(data);
        console.log('[ExpenseForm] Expense created successfully:', expenseId);
        return expenseId;
      } catch (error) {
        console.error('[ExpenseForm] Error saving expense:', error);
        if (error instanceof Error) {
          console.error('[ExpenseForm] Error details:', {
            message: error.message,
            stack: error.stack,
            name: error.name
          });
        }
        throw error;
      }
    },
    onSuccess: (expenseId) => {
      console.log('[ExpenseForm] Mutation successful, navigating to expense:', expenseId);
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expense', expenseId] });
      navigate(`/expenses/${expenseId}`);
    },
    onError: (error: unknown) => {
      console.error('[ExpenseForm] Mutation error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save expense';
      console.error('[ExpenseForm] Error message:', errorMessage);
      if (error instanceof Error && error.message.includes('permission')) {
        setErrorMessage(`Permission denied: ${errorMessage}. Please ensure you are logged in as operations staff.`);
      } else {
        setErrorMessage(errorMessage);
      }
    },
  });

  const addTag = () => {
    const next = tagInput.trim();
    if (!next) return;
    if (formData.tags?.includes(next)) {
      setTagInput('');
      return;
    }
    setFormData((prev) => ({ ...prev, tags: [...(prev.tags || []), next] }));
    setTagInput('');
  };

  const removeTag = (tag: string) => {
    setFormData((prev) => ({ ...prev, tags: prev.tags?.filter((t) => t !== tag) || [] }));
  };

  const filteredVendors = vendors.filter((v) =>
    v.name.toLowerCase().includes(vendorSearch.toLowerCase()) ||
    (v.email || '').toLowerCase().includes(vendorSearch.toLowerCase()) ||
    (v.contactName || '').toLowerCase().includes(vendorSearch.toLowerCase())
  );

  const handleVendorSelect = (vendor: Vendor) => {
    console.log('[ExpenseForm] Selecting vendor:', vendor);
    setFormData((prev) => ({
      ...prev,
      vendorId: vendor.id,
      vendorName: vendor.name,
      merchant: vendor.name, // Auto-fill merchant name
    }));
    setVendorSearch(vendor.name);
    setShowVendorDropdown(false);
    console.log('[ExpenseForm] Vendor selected, formData updated');
  };

  const handleVendorSearchChange = (value: string) => {
    setVendorSearch(value);
    setShowVendorDropdown(true);
    if (!value) {
      setFormData((prev) => ({
        ...prev,
        vendorId: undefined,
        vendorName: undefined,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    try {
      await mutation.mutateAsync(formData);
    } catch (err) {
      console.error('[ExpenseForm] Submit error:', err);
    }
  };

  const handleSaveAsDraft = async () => {
    setErrorMessage(null);
    try {
      await mutation.mutateAsync({ ...formData, status: ExpenseStatus.draft });
    } catch (err) {
      console.error('[ExpenseForm] Save draft error:', err);
    }
  };

  const handleSubmitForApproval = async () => {
    setErrorMessage(null);
    try {
      await mutation.mutateAsync({ ...formData, status: ExpenseStatus.submitted, submittedAt: new Date() });
    } catch (err) {
      console.error('[ExpenseForm] Submit for approval error:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading expense...</p>
        </div>
      </div>
    );
  }

  if (isEditing && id && expense === null) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white shadow rounded-lg p-6">
          <h1 className="text-xl font-semibold text-gray-900">Expense not found</h1>
          <p className="mt-2 text-sm text-gray-600">The requested expense does not exist or you may not have access.</p>
          <div className="mt-4">
            <Link
              to="/expenses"
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Back to Expenses
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Link to="/expenses" className="text-blue-600 hover:text-blue-700 text-sm">
          ← Back to Expenses
        </Link>
      </div>

      <h1 className="text-2xl font-bold text-gray-900 mb-6">{isEditing ? 'Edit Expense' : 'Add New Expense'}</h1>

      <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-6 space-y-6">
        {errorMessage && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-700">{errorMessage}</p>
          </div>
        )}

        {/* Basic Information */}
        <div className="border-b pb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Description *</label>
              <input
                type="text"
                required
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="e.g., Client lunch meeting"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Category *</label>
              <select
                required
                value={formData.category || ExpenseCategory.other}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as ExpenseCategory })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {Object.entries(ExpenseCategoryGroups).map(([group, categories]) => (
                  <optgroup key={group} label={group}>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {ExpenseCategoryLabels[cat]}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Expense Date *</label>
              <input
                type="date"
                required
                value={formData.expenseDate ? format(formData.expenseDate, 'yyyy-MM-dd') : ''}
                onChange={(e) => setFormData({ ...formData, expenseDate: new Date(e.target.value) })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {!showMileageFields && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Amount *</label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">$</span>
                    </div>
                    <input
                      type="number"
                      required
                      step="0.01"
                      min="0"
                      value={formData.amount || ''}
                      onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                      className="block w-full pl-7 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Currency</label>
                  <select
                    value={formData.currency || 'USD'}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                    <option value="CAD">CAD - Canadian Dollar</option>
                    <option value="AUD">AUD - Australian Dollar</option>
                  </select>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Mileage Details */}
        {showMileageFields && (
          <div className="border-b pb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Mileage Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Start Location *</label>
                <input
                  type="text"
                  required
                  value={mileageData.startLocation || ''}
                  onChange={(e) => setMileageData({ ...mileageData, startLocation: e.target.value })}
                  placeholder="e.g., Office, 123 Main St"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">End Location *</label>
                <input
                  type="text"
                  required
                  value={mileageData.endLocation || ''}
                  onChange={(e) => setMileageData({ ...mileageData, endLocation: e.target.value })}
                  placeholder="e.g., Client Office, 456 Oak Ave"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Miles (one way) *</label>
                <input
                  type="number"
                  required
                  step="0.1"
                  min="0"
                  value={mileageData.miles || ''}
                  onChange={(e) => setMileageData({ ...mileageData, miles: parseFloat(e.target.value) || 0 })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Rate per Mile</label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={mileageData.ratePerMile || 0.67}
                    onChange={(e) => setMileageData({ ...mileageData, ratePerMile: parseFloat(e.target.value) || 0.67 })}
                    className="block w-full pl-7 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="roundTrip"
                  checked={mileageData.roundTrip || false}
                  onChange={(e) => setMileageData({ ...mileageData, roundTrip: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="roundTrip" className="ml-2 block text-sm text-gray-700">
                  Round Trip
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Purpose</label>
                <input
                  type="text"
                  value={mileageData.purpose || ''}
                  onChange={(e) => setMileageData({ ...mileageData, purpose: e.target.value })}
                  placeholder="e.g., Client meeting"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {mileageData.miles && mileageData.ratePerMile && (
                <div className="md:col-span-2 bg-blue-50 rounded-md p-4">
                  <p className="text-sm text-blue-700">
                    <strong>Calculated Amount:</strong> {mileageData.miles} miles × ${mileageData.ratePerMile}/mile
                    {mileageData.roundTrip ? ' × 2 (round trip)' : ''} ={' '}
                    <strong>
                      ${(mileageData.miles * mileageData.ratePerMile * (mileageData.roundTrip ? 2 : 1)).toFixed(2)}
                    </strong>
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Payment Details */}
        <div className="border-b pb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Payment Method</label>
              <select
                value={formData.paymentMethod || PaymentMethod.personalCard}
                onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value as PaymentMethod })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {Object.entries(PaymentMethodLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {(formData.paymentMethod === PaymentMethod.companyCard ||
              formData.paymentMethod === PaymentMethod.personalCard) && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Card Last 4 Digits</label>
                <input
                  type="text"
                  maxLength={4}
                  value={formData.cardLastFour || ''}
                  onChange={(e) => setFormData({ ...formData, cardLastFour: e.target.value.replace(/\D/g, '') })}
                  placeholder="1234"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}
          </div>
        </div>

        {/* Merchant/Vendor Details */}
        <div className="border-b pb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Merchant/Vendor Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700">Vendor</label>
              <div className="mt-1 relative">
                <input
                  type="text"
                  value={vendorSearch}
                  onChange={(e) => handleVendorSearchChange(e.target.value)}
                  onFocus={() => setShowVendorDropdown(true)}
                  onBlur={() => {
                    // Delay to allow click on dropdown item
                    setTimeout(() => setShowVendorDropdown(false), 200);
                  }}
                  placeholder="Search or select a vendor..."
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 pr-10 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
                {formData.vendorId && (
                  <button
                    type="button"
                    onClick={() => {
                      setVendorSearch('');
                      setFormData((prev) => ({
                        ...prev,
                        vendorId: undefined,
                        vendorName: undefined,
                      }));
                    }}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                    title="Clear vendor"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
                {showVendorDropdown && vendorSearch && filteredVendors.length > 0 && (
                  <div 
                    className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
                    onMouseDown={(e) => e.preventDefault()} // Prevent blur when clicking inside dropdown
                  >
                    {filteredVendors.map((vendor) => (
                      <button
                        key={vendor.id}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleVendorSelect(vendor);
                        }}
                        className="w-full text-left px-4 py-2 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
                      >
                        <div className="font-medium text-gray-900">{vendor.name}</div>
                        {vendor.email && (
                          <div className="text-sm text-gray-500">{vendor.email}</div>
                        )}
                        {vendor.contactName && (
                          <div className="text-xs text-gray-400">{vendor.contactName}</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                {showVendorDropdown && vendorSearch && filteredVendors.length === 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
                    <div className="px-4 py-2 text-sm text-gray-500">No vendors found</div>
                  </div>
                )}
              </div>
              {formData.vendorId && (
                <p className="mt-1 text-xs text-gray-500">Vendor selected: {formData.vendorName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Merchant Name</label>
              <input
                type="text"
                value={formData.merchant || ''}
                onChange={(e) => setFormData({ ...formData, merchant: e.target.value })}
                placeholder="e.g., Starbucks, United Airlines"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Location</label>
              <input
                type="text"
                value={formData.location || ''}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="e.g., 123 Main St, New York"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">City</label>
              <input
                type="text"
                value={formData.city || ''}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">State</label>
              <input
                type="text"
                value={formData.state || ''}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Project & Billing */}
        <div className="border-b pb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Project & Billing</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Project Name</label>
              <input
                type="text"
                value={formData.projectName || ''}
                onChange={(e) => setFormData({ ...formData, projectName: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Project Code</label>
              <input
                type="text"
                value={formData.projectCode || ''}
                onChange={(e) => setFormData({ ...formData, projectCode: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Department</label>
              <input
                type="text"
                value={formData.departmentName || ''}
                onChange={(e) => setFormData({ ...formData, departmentName: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Cost Center</label>
              <input
                type="text"
                value={formData.costCenterName || ''}
                onChange={(e) => setFormData({ ...formData, costCenterName: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="billable"
                checked={formData.billable || false}
                onChange={(e) => setFormData({ ...formData, billable: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="billable" className="ml-2 block text-sm text-gray-700">
                Billable to Client
              </label>
            </div>

            {formData.billable && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Client Name</label>
                <input
                  type="text"
                  value={formData.clientName || ''}
                  onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}
          </div>
        </div>

        {/* Tax Information */}
        <div className="border-b pb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Tax Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Tax Amount</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.taxAmount || ''}
                  onChange={(e) => setFormData({ ...formData, taxAmount: parseFloat(e.target.value) || 0 })}
                  className="block w-full pl-7 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="flex items-center pt-6">
              <input
                type="checkbox"
                id="taxDeductible"
                checked={formData.taxDeductible ?? true}
                onChange={(e) => setFormData({ ...formData, taxDeductible: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="taxDeductible" className="ml-2 block text-sm text-gray-700">
                Tax Deductible
              </label>
            </div>
          </div>
        </div>

        {/* Notes & Tags */}
        <div className="border-b pb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes & Tags</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Notes</label>
              <textarea
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                placeholder="Add any additional notes about this expense..."
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Tags</label>
              <div className="mt-1 flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  placeholder="Add a tag and press Enter"
                  className="flex-1 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  type="button"
                  onClick={addTag}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Add
                </button>
              </div>
              {formData.tags && formData.tags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {formData.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {tag}
                      <button type="button" onClick={() => removeTag(tag)} className="ml-1 text-blue-600 hover:text-blue-800">
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Receipt Upload Placeholder */}
        <div className="border-b pb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Receipt</h2>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="mt-4">
              <label htmlFor="receipt-upload" className="cursor-pointer">
                <span className="text-blue-600 hover:text-blue-500 font-medium">Upload a receipt</span>
                <span className="text-gray-500"> or drag and drop</span>
                <input id="receipt-upload" type="file" className="sr-only" accept="image/*,.pdf" />
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-2">PNG, JPG, PDF up to 10MB</p>
          </div>
          {formData.receiptUrl && (
            <div className="mt-3 flex items-center justify-between bg-gray-50 rounded-md p-3">
              <div className="flex items-center">
                <svg className="h-5 w-5 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <span className="text-sm text-gray-600">{formData.receiptFileName || 'Receipt attached'}</span>
              </div>
              <a
                href={formData.receiptUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:text-blue-500"
              >
                View
              </a>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/expenses')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSaveAsDraft}
            disabled={mutation.isPending}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            {mutation.isPending ? 'Saving...' : 'Save as Draft'}
          </button>
          <button
            type="button"
            onClick={handleSubmitForApproval}
            disabled={mutation.isPending}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
          >
            {mutation.isPending ? 'Submitting...' : 'Submit for Approval'}
          </button>
        </div>
      </form>
    </div>
  );
};
